import React from "react";
import { BlockMath, ChoiceLabel, ChoiceOptionMath, InlineMath } from "../../../components/math/KatexWrappers";
import ABCDInput from "../../../components/inputs/ABCDInput";
import GraphViewer from "../../../components/GraphPlotter/GraphViewer";

const LETTERS = ["A", "B", "C", "D"];

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function pointLatex(point) {
  return `\\left(${point.x},\\ ${point.y}\\right)`;
}

function optionLabel(point) {
  return pointLatex(point);
}

function wrapNegative(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function samePoint(a, b) {
  return a.x === b.x && a.y === b.y;
}

function buildOptions(correct, distractors, keepOrder = false) {
  const points = [correct, ...distractors.filter((p) => !samePoint(p, correct))];
  const unique = [];
  points.forEach((point) => {
    if (!unique.some((item) => samePoint(item, point))) unique.push(point);
  });
  let offset = 1;
  while (unique.length < 4) {
    const point = {
      x: correct.x + offset,
      y: correct.y + (offset % 2 === 0 ? 1 : 0),
    };
    if (!unique.some((item) => samePoint(item, point))) unique.push(point);
    offset += 1;
  }
  const ordered = (keepOrder ? unique : shuffle(unique)).slice(0, 4).map((point) => ({
    value: point,
    latex: optionLabel(point),
  }));
  return {
    options: ordered,
    correctIndex: ordered.findIndex((option) => samePoint(option.value, correct)),
  };
}

function buildData({ A, C, D, options = null }) {
  const B = {
    x: A.x + C.x - D.x,
    y: A.y + C.y - D.y,
  };
  const builtOptions =
    options ||
    buildOptions(B, [
      { x: C.x, y: A.y },
      { x: B.x + 1, y: B.y },
      { x: B.x + 2, y: B.y },
    ]);

  return {
    A,
    B,
    C,
    D,
    options: builtOptions.options,
    correctIndex: builtOptions.correctIndex,
  };
}

function createOriginalTask() {
  return {
    type: "E8ParallelogramVertexClosedTask",
    data: buildData({
      A: { x: -3, y: -2 },
      C: { x: 4, y: 2 },
      D: { x: -1, y: 2 },
      options: {
        options: [
          { x: 4, y: -2 },
          { x: 3, y: -2 },
          { x: 2, y: -2 },
          { x: 6, y: -2 },
        ].map((point) => ({ value: point, latex: optionLabel(point) })),
        correctIndex: 2,
      },
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 14,
    },
    tags: ["e8", "geometria-analityczna", "wspolrzedne", "rownoleglobok"],
  };
}

function randomInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const A = { x: randomInt(-5, -1), y: randomInt(-4, 0) };
  const width = randomInt(3, 6);
  const rise = randomInt(1, 4);
  const D = { x: A.x + randomInt(-1, 1), y: A.y + rise };
  const B = { x: Math.max(1, A.x + width), y: A.y };
  const C = { x: B.x + D.x - A.x, y: D.y };

  return {
    type: "E8ParallelogramVertexClosedTask",
    data: buildData({
      A,
      C,
      D,
      options: buildOptions(B, [
        { x: C.x, y: A.y },
        { x: B.x + 1, y: B.y },
        { x: B.x + 2, y: B.y },
      ]),
    }),
    source: { variant: "random", basedOn: "E8_2025_MAJ_14" },
    tags: ["e8", "geometria-analityczna", "wspolrzedne", "rownoleglobok"],
  };
}

function CoordinateDiagram({ data, revealB = false, showConstruction = false }) {
  const pointColor = "#cc5500";
  const constructionColor = "#7c3aed";
  const points = {
    A: { ...data.A, color: pointColor, textPosition: "bottom left" },
    C: { ...data.C, color: pointColor, textPosition: "top right" },
    D: { ...data.D, color: pointColor, textPosition: "top left" },
  };

  if (revealB || showConstruction) {
    points.B = { ...data.B, color: constructionColor, textPosition: "bottom right" };
  }

  const segments = showConstruction
    ? [
        { p1: "D", p2: "C", color: constructionColor, width: 4 },
        { p1: "A", p2: "B", color: constructionColor, width: 4 },
        { p1: "D", p2: "A", color: "rgba(124,58,237,0.68)", width: 3 },
        { p1: "C", p2: "B", color: "rgba(124,58,237,0.68)", width: 3 },
      ]
    : [];

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0", overflowX: "auto" }}>
      <GraphViewer
        points={points}
        segments={segments}
        xRange={[-7, 7]}
        yRange={[-5, 5]}
        width={420}
        height={320}
        squareFrame={false}
      />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W układzie współrzędnych zaznaczono trzy punkty, które są wierzchołkami
        równoległoboku <InlineMath math="ABCD" />:
      </p>
      <p>
        <InlineMath math={`A=${pointLatex(data.A)}`} />,{" "}
        <InlineMath math={`C=${pointLatex(data.C)}`} />,{" "}
        <InlineMath math={`D=${pointLatex(data.D)}`} /> (zobacz rysunek).
      </p>
      <CoordinateDiagram data={data} />
      <p>
        Współrzędna <InlineMath math="x" /> wierzchołka <InlineMath math="B" />,
        niezaznaczonego na rysunku, jest liczbą dodatnią.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Współrzędne punktu <InlineMath math="B" /> są równe</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.latex}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={data.options[data.correctIndex].latex} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const dcX = data.C.x - data.D.x;
  const dcY = data.C.y - data.D.y;
  const daX = data.A.x - data.D.x;
  const daY = data.A.y - data.D.y;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <CoordinateDiagram data={data} revealB showConstruction />
      <p>
        <strong>Krok 1.</strong> W równoległoboku przeciwległe boki są równoległe
        i mają taką samą długość. Możemy więc przenieść wektor od{" "}
        <InlineMath math="D" /> do <InlineMath math="C" /> tak, aby zaczynał się
        w punkcie <InlineMath math="A" />.
      </p>
      <BlockMath math={`\\overrightarrow{DC}=(${wrapNegative(data.C.x)}-${wrapNegative(data.D.x)},\\ ${wrapNegative(data.C.y)}-${wrapNegative(data.D.y)})=(${dcX},\\ ${dcY})`} />
      <BlockMath math={`B=A+\\overrightarrow{DC}=(${data.A.x},\\ ${data.A.y})+(${dcX},\\ ${dcY})=${pointLatex(data.B)}`} />
      <p>
        <strong>Sprawdzenie.</strong> Można też przenieść wektor od{" "}
        <InlineMath math="D" /> do <InlineMath math="A" /> tak, aby zaczynał się
        w punkcie <InlineMath math="C" />.
      </p>
      <BlockMath math={`\\overrightarrow{DA}=(${wrapNegative(data.A.x)}-${wrapNegative(data.D.x)},\\ ${wrapNegative(data.A.y)}-${wrapNegative(data.D.y)})=(${daX},\\ ${daY})`} />
      <BlockMath math={`B=C+\\overrightarrow{DA}=(${data.C.x},\\ ${data.C.y})+(${daX},\\ ${daY})=${pointLatex(data.B)}`} />
      <p>
        Poprawna odpowiedź to <ChoiceLabel letter={LETTERS[data.correctIndex]} />
      </p>
    </div>
  );
}
